// Helper to request customer enrichment from the backend
import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import { toast } from 'react-hot-toast';
import { X, Check, ExternalLink } from 'lucide-react';

const ENRICH_PROGRESS_TOAST_ID = 'customer-enrich-progress';

const renderProgressToast = (title, message, progress, status = 'info') => (
  <div className="w-72 max-w-sm px-4 py-3 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
    <div className="space-y-2">
      <div className="text-sm font-semibold text-gray-900 dark:text-white">{title}</div>
      {message && (
        <div className="text-xs text-gray-600 dark:text-gray-400 leading-snug">
          {message}
        </div>
      )}
      <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-full transition-all duration-300 ${status === 'error' ? 'bg-red-500 dark:bg-red-400' : 'bg-blue-500 dark:bg-blue-400'}`}
          style={{ width: `${Math.max(0, Math.min(progress, 100))}%` }}
        />
      </div>
    </div>
  </div>
);

const updateProgressToast = (progress, message, status = 'info') => {
  if (typeof window === 'undefined') return;
  toast.custom(() => renderProgressToast(
    status === 'error' ? 'Enrichment Failed' : 'Enriching Customer',
    message,
    progress,
    status
  ), {
    id: ENRICH_PROGRESS_TOAST_ID,
    duration: Infinity,
    position: 'top-right'
  });
};

const dismissProgressToast = (delay = 600) => {
  if (typeof window === 'undefined') return;
  setTimeout(() => toast.dismiss(ENRICH_PROGRESS_TOAST_ID), delay);
};

// Enrichment options dialog component
const EnrichmentOptionsDialog = ({ customerType, onConfirm, onCancel }) => {
  const [selectedOptions, setSelectedOptions] = useState({
    basicInfo: true,
    contactInfo: true,
    socialMedia: true,
    companyInfo: customerType === 'Company',
    personInfo: customerType === 'Person'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toggleOption = (option) => {
    setSelectedOptions(prev => ({
      ...prev,
      [option]: !prev[option]
    }));
  };

  const selectAll = () => {
    setSelectedOptions({
      basicInfo: true,
      contactInfo: true,
      socialMedia: true,
      companyInfo: customerType === 'Company',
      personInfo: customerType === 'Person'
    });
  };

  const deselectAll = () => {
    setSelectedOptions({
      basicInfo: false,
      contactInfo: false,
      socialMedia: false,
      companyInfo: false,
      personInfo: false
    });
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    await onConfirm(selectedOptions);
    setIsSubmitting(false);
  };

  const hasSelection = Object.values(selectedOptions).some(v => v === true);

  const options = [
    {
      key: 'basicInfo',
      label: 'Basic Information',
      description: 'Name, title, company name, industry',
      icon: '📋',
      available: true
    },
    {
      key: 'contactInfo',
      label: 'Contact Information',
      description: 'Email, phone, address, city, country, tax ID, company email',
      icon: '📞',
      available: true
    },
    {
      key: 'socialMedia',
      label: 'Social Media Profiles',
      description: 'Facebook, LinkedIn, Twitter, Instagram, YouTube, LINE, TikTok',
      icon: '🔗',
      available: true
    },
    {
      key: 'companyInfo',
      label: 'Company Details',
      description: 'Company name (English), industry, website, branch, tax ID, company email',
      icon: '🏢',
      available: customerType === 'Company'
    },
    {
      key: 'personInfo',
      label: 'Personal Details',
      description: 'First name, last name, job title, English names',
      icon: '👤',
      available: customerType === 'Person'
    }
  ].filter(opt => opt.available);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Select Enrichment Areas
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Choose which information you want to enrich for this customer
            </p>
          </div>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Select All / Deselect All */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={selectAll}
              disabled={isSubmitting}
              className="text-xs px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-50"
            >
              Select All
            </button>
            <button
              onClick={deselectAll}
              disabled={isSubmitting}
              className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Deselect All
            </button>
          </div>

          {/* Options List */}
          <div className="space-y-3">
            {options.map((option) => {
              const isSelected = selectedOptions[option.key];

              return (
                <label
                  key={option.key}
                  className={`flex items-start p-4 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleOption(option.key)}
                    disabled={isSubmitting}
                    className="mt-1 mr-3 h-5 w-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl">{option.icon}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {option.label}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {option.description}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || !hasSelection}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Starting...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Start Enrichment
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper to show enrichment options dialog
const showEnrichmentOptionsDialog = (customerType) => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    let root = null;

    const handleConfirm = async (options) => {
      if (root) {
        root.unmount();
      }
      document.body.removeChild(container);
      resolve(options);
    };

    const handleCancel = () => {
      if (root) {
        root.unmount();
      }
      document.body.removeChild(container);
      resolve(null);
    };

    root = createRoot(container);
    root.render(
      React.createElement(EnrichmentOptionsDialog, {
        customerType: customerType || 'Person',
        onConfirm: handleConfirm,
        onCancel: handleCancel
      })
    );
  });
};

// Confirmation dialog component for social media URLs
const SocialMediaConfirmationDialog = ({ urls, onConfirm, onCancel }) => {
  const [selectedUrls, setSelectedUrls] = useState(new Set(urls.map(u => u.url)));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const platformNames = {
    facebook: 'Facebook',
    linkedin: 'LinkedIn',
    twitter: 'Twitter',
    x: 'X (Twitter)',
    instagram: 'Instagram',
    youtube: 'YouTube',
    line: 'LINE',
    tiktok: 'TikTok'
  };

  const platformEmojis = {
    facebook: '📘',
    linkedin: '💼',
    twitter: '🐦',
    x: '🐦',
    instagram: '📷',
    youtube: '📺',
    line: '💬',
    tiktok: '🎵'
  };

  const toggleUrl = (url) => {
    const newSelected = new Set(selectedUrls);
    if (newSelected.has(url)) {
      newSelected.delete(url);
    } else {
      newSelected.add(url);
    }
    setSelectedUrls(newSelected);
  };

  const selectAll = () => {
    setSelectedUrls(new Set(urls.map(u => u.url)));
  };

  const deselectAll = () => {
    setSelectedUrls(new Set());
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);
    const selected = urls.filter(u => selectedUrls.has(u.url));
    await onConfirm(selected);
    setIsSubmitting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Social Media URLs Found
          </h3>
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Select the social media URLs you want to attach to this customer:
          </p>

          {/* Select All / Deselect All */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={selectAll}
              disabled={isSubmitting}
              className="text-xs px-3 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded hover:bg-blue-200 dark:hover:bg-blue-800 disabled:opacity-50"
            >
              Select All
            </button>
            <button
              onClick={deselectAll}
              disabled={isSubmitting}
              className="text-xs px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Deselect All
            </button>
          </div>

          {/* URL List */}
          <div className="space-y-2">
            {urls.map((social, index) => {
              const platformName = platformNames[social.platform?.toLowerCase()] || social.platform || 'Social Media';
              const emoji = platformEmojis[social.platform?.toLowerCase()] || '🔗';
              const isSelected = selectedUrls.has(social.url);

              return (
                <label
                  key={index}
                  className={`flex items-start p-3 border rounded-lg cursor-pointer transition-colors ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleUrl(social.url)}
                    disabled={isSubmitting}
                    className="mt-1 mr-3 h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{emoji}</span>
                      <span className="font-medium text-gray-900 dark:text-white">
                        {platformName}
                      </span>
                    </div>
                    <a
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 break-all"
                    >
                      {social.url}
                      <ExternalLink className="h-3 w-3 flex-shrink-0" />
                    </a>
                    {social.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {social.description}
                      </p>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSubmitting || selectedUrls.size === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Attaching...
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Attach Selected ({selectedUrls.size})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper to render confirmation dialog using React Portal
const showConfirmationDialog = (urls) => {
  return new Promise((resolve) => {
    const container = document.createElement('div');
    document.body.appendChild(container);

    let root = null;

    const handleConfirm = async (selected) => {
      if (root) {
        root.unmount();
      }
      document.body.removeChild(container);
      resolve(selected);
    };

    const handleCancel = () => {
      if (root) {
        root.unmount();
      }
      document.body.removeChild(container);
      resolve([]);
    };

    root = createRoot(container);
    root.render(
      React.createElement(SocialMediaConfirmationDialog, {
        urls,
        onConfirm: handleConfirm,
        onCancel: handleCancel
      })
    );
  });
};

/**
 * Enrich customer data via backend API
 * @param {number|string} customerId
 * @param {object} options
 * @param {string} [options.languagePreference] optional 'th' to request Thai
 * @param {boolean} [options.confirmAttachSocialMedia] if true, prompt user to attach each social URL
 * @param {string} [options.customerType] Customer type ('Person' or 'Company') for options dialog
 * @param {boolean} [options.skipOptionsDialog] If true, skip the options dialog and enrich all areas
 * @param {object} [options.enrichmentAreas] Pre-selected enrichment areas (if skipOptionsDialog is true)
 * @returns {Promise<any>} parsed JSON response
 */
export async function customerEnrish(customerId, options = {}) {
  if (!customerId) throw new Error('Customer ID is required');

  // Show enrichment options dialog unless skipped
  let enrichmentAreas = options.enrichmentAreas;
  if (!options.skipOptionsDialog) {
    const customerType = options.customerType || 'Person';
    enrichmentAreas = await showEnrichmentOptionsDialog(customerType);
    
    if (!enrichmentAreas) {
      // User cancelled
      throw new Error('Enrichment cancelled by user');
    }
  }

  // If no areas selected, use defaults (all areas)
  if (!enrichmentAreas) {
    enrichmentAreas = {
      basicInfo: true,
      contactInfo: true,
      socialMedia: true,
      companyInfo: options.customerType === 'Company',
      personInfo: options.customerType === 'Person'
    };
  }

  const payload = {
    enrichmentAreas
  };
  if (options.languagePreference) payload.languagePreference = options.languagePreference;

  updateProgressToast(10, 'Preparing enrichment request...');

  try {
    const response = await fetch(`/api/customers/${customerId}/enrich`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payload)
    });

    updateProgressToast(55, 'Processing enrichment response...');

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to enrich customer data');
    }

    const result = await response.json();

    updateProgressToast(85, 'Applying enrichment results...');

    // Check for existing attachments and show confirmation dialog before attaching social media URLs
    try {
      const socialMediaUrls = [];
      
      // Collect social media URLs from attachments array
      if (result && Array.isArray(result.attachments)) {
        for (const att of result.attachments) {
          if (att && att.type === 'url' && att.tag === 'socialmedia' && att.url) {
            socialMediaUrls.push({
              url: att.url,
              platform: att.platform || 'unknown',
              title: att.title || `${att.platform || 'Social Media'} Profile`,
              description: att.description || ''
            });
          }
        }
      }
      
      // Also check socialMediaUrls array if attachments weren't populated
      if (socialMediaUrls.length === 0 && result && Array.isArray(result.socialMediaUrls)) {
        for (const social of result.socialMediaUrls) {
          if (social && social.url) {
            socialMediaUrls.push({
              url: social.url,
              platform: social.platform || 'unknown',
              title: social.title || `${social.platform || 'Social Media'} Profile`,
              description: social.description || ''
            });
          }
        }
      }

      // Check for existing attachments to avoid duplicates
      if (socialMediaUrls.length > 0) {
        updateProgressToast(88, 'Checking for existing attachments...');
        
        const tenantCode = localStorage.getItem('tenantCode') || '';
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        };
        if (tenantCode) {
          headers['X-Tenant-Code'] = tenantCode;
        }

        // Fetch existing attachments
        let existingAttachments = [];
        try {
          const existingResponse = await fetch(`/api/attachments/Customer/${customerId}`, {
            method: 'GET',
            headers
          });

          if (existingResponse.ok) {
            const existingData = await existingResponse.json();
            existingAttachments = existingData.attachments || [];
          }
        } catch (err) {
          console.warn('Failed to fetch existing attachments:', err);
        }

        // Extract existing URLs (normalize for comparison)
        const normalizeUrl = (url) => {
          try {
            const urlObj = new URL(url);
            // Remove trailing slashes and normalize
            return `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname.replace(/\/$/, '')}${urlObj.search}`.toLowerCase();
          } catch {
            return url.toLowerCase().trim();
          }
        };

        const existingUrls = new Set(
          existingAttachments
            .filter(att => att.AttachmentType === 'URL' && att.URL)
            .map(att => normalizeUrl(att.URL))
        );

        // Filter out duplicates
        const newUrls = socialMediaUrls.filter(social => {
          const normalized = normalizeUrl(social.url);
          return !existingUrls.has(normalized);
        });

        // Show confirmation dialog if there are new URLs to attach
        if (newUrls.length > 0) {
          updateProgressToast(90, `Found ${newUrls.length} new social media link(s)...`);
          
          // Show confirmation dialog
          const selectedUrls = await showConfirmationDialog(newUrls);

          if (selectedUrls.length > 0) {
            updateProgressToast(92, `Attaching ${selectedUrls.length} social media link(s)...`);
            
            const attachmentPromises = selectedUrls.map(async (social) => {
              try {
                const platformNames = {
                  facebook: 'Facebook',
                  linkedin: 'LinkedIn',
                  twitter: 'Twitter',
                  x: 'X (Twitter)',
                  instagram: 'Instagram',
                  youtube: 'YouTube',
                  line: 'LINE',
                  tiktok: 'TikTok'
                };
                
                const platformName = platformNames[social.platform?.toLowerCase()] || social.platform || 'Social Media';
                const title = social.title || `${platformName} Profile`;
                
                const attachResponse = await fetch(`/api/attachments/Customer/${customerId}/url`, {
                  method: 'POST',
                  headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    url: social.url,
                    title: title,
                    description: social.description || `Social media profile found during enrichment`,
                    tags: `socialmedia,${social.platform || 'social'}`
                  })
                });

                if (!attachResponse.ok) {
                  const errorData = await attachResponse.json().catch(() => ({}));
                  console.warn(`Failed to attach social media URL ${social.url}:`, errorData.message || attachResponse.statusText);
                  return { success: false, url: social.url, error: errorData.message || attachResponse.statusText };
                }

                return { success: true, url: social.url };
              } catch (error) {
                console.warn(`Error attaching social media URL ${social.url}:`, error.message);
                return { success: false, url: social.url, error: error.message };
              }
            });

            const attachmentResults = await Promise.all(attachmentPromises);
            const successful = attachmentResults.filter(r => r.success).length;
            const failed = attachmentResults.filter(r => !r.success).length;

            if (successful > 0) {
              updateProgressToast(95, `Attached ${successful} social media link(s)`);
            }
            
            if (failed > 0) {
              console.warn(`${failed} social media URL(s) failed to attach`);
            }

            // Store attachment results in the result object
            result.attachmentResults = {
              total: newUrls.length,
              selected: selectedUrls.length,
              successful,
              failed,
              skipped: newUrls.length - selectedUrls.length,
              duplicates: socialMediaUrls.length - newUrls.length,
              results: attachmentResults
            };
          } else {
            // User cancelled or selected none
            result.attachmentResults = {
              total: newUrls.length,
              selected: 0,
              successful: 0,
              failed: 0,
              skipped: newUrls.length,
              duplicates: socialMediaUrls.length - newUrls.length
            };
          }
        } else if (socialMediaUrls.length > 0) {
          // All URLs are duplicates
          result.attachmentResults = {
            total: socialMediaUrls.length,
            selected: 0,
            successful: 0,
            failed: 0,
            skipped: 0,
            duplicates: socialMediaUrls.length
          };
          updateProgressToast(92, 'All social media URLs already attached');
        }
      }
    } catch (error) {
      // Don't fail the whole enrichment if attachment fails
      console.warn('Error processing social media URLs:', error);
    }

    // Optional: confirm attaching social media URLs one-by-one on the client (legacy option)
    try {
      if (options.confirmAttachSocialMedia && result && Array.isArray(result.attachments)) {
        const confirmed = [];
        const others = [];
        for (const att of result.attachments) {
          if (att && att.type === 'url' && att.tag === 'socialmedia' && att.url) {
            // Ask for confirmation per URL
            // eslint-disable-next-line no-alert
            const ok = window.confirm(`Attach social media link?\n\n${att.url}`);
            if (ok) confirmed.push(att);
          } else {
            others.push(att);
          }
        }
        // Preserve original attachments, but also provide a filtered list
        result.confirmedAttachments = {
          socialmedia: confirmed,
          others
        };
      }
    } catch (_) {
      // Non-browser environments: ignore confirmation
    }

    updateProgressToast(100, 'Enrichment complete!');
    dismissProgressToast();

    return result;
  } catch (error) {
    updateProgressToast(100, error.message || 'Failed to enrich customer data', 'error');
    dismissProgressToast(2000);
    throw error;
  }
}


